/**
 * «Свой ответ…» — an auto-growing textarea with a ➤ confirm, capped at the
 * round's `custom_max_chars` (index amendment A3), with a counter once the text is within 20%
 * of the cap. Enter confirms (Shift+Enter is a new line; IME composition is
 * left alone), mirroring the composer (MessageInput.tsx:444-449). Voice input
 * is not in v1 (spec): the composer's mic works after ✕.
 */
import { ArrowUp } from "lucide-react";
import { useEffect, useId, useRef, type KeyboardEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { iconProps } from "@/lib/icons";

import type { ClarifySkin } from "./skin";

/** The field grows with its text up to this height, then scrolls. */
const CUSTOM_MAX_HEIGHT_PX = 120;

export interface ClarifyCustomAnswerProps {
  value: string;
  max: number;
  /** Focus on mount: the user just chose «Свой ответ…». */
  autoFocus: boolean;
  skin: ClarifySkin;
  onChange: (value: string) => void;
  onConfirm: () => void;
}

export function ClarifyCustomAnswer({
  value,
  max,
  autoFocus,
  skin,
  onChange,
  onConfirm,
}: ClarifyCustomAnswerProps): ReactElement {
  const { t } = useTranslation("chat");
  const counterId = useId();
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (autoFocus) fieldRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const field = fieldRef.current;
    if (field === null) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, CUSTOM_MAX_HEIGHT_PX)}px`;
  }, [value]);

  const nearLimit = value.length >= Math.ceil(max * 0.8);
  const canConfirm = value.trim().length > 0;

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canConfirm) onConfirm();
  };

  return (
    <div className="mt-2">
      <div className={skin.customField}>
        <textarea
          ref={fieldRef}
          rows={1}
          value={value}
          maxLength={max}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("clarify.customPlaceholder")}
          aria-label={t("clarify.customPlaceholder")}
          aria-describedby={nearLimit ? counterId : undefined}
          data-testid="clarify-custom-input"
          className={skin.customInput}
        />
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canConfirm}
          aria-label={t("clarify.customConfirm")}
          data-testid="clarify-custom-confirm"
          className={skin.confirmButton}
        >
          <ArrowUp {...iconProps("md")} />
        </button>
      </div>
      {nearLimit ? (
        <p id={counterId} data-testid="clarify-custom-counter" className={skin.counter}>
          {t("clarify.customCounter", { used: value.length, max })}
        </p>
      ) : null}
    </div>
  );
}
